import { useMemo, useState } from 'react';
import { useUser } from '../context/UserContext';
import { cargarCampaniaDirecto, getDetalleOto } from '../api/apiConfig';
import { Link } from 'react-router-dom';

const CargaCampana = () => {
  const { user } = useUser();
  const [campanaNombre, setCampanaNombre] = useState('');
  const [fecIniCam, setFecIniCam] = useState('');
  const [fecFinCam, setFecFinCam] = useState('');
  const [loading, setLoading] = useState(false);
  const [lastResult, setLastResult] = useState(null);

  const canSubmit = useMemo(() => {
    return !!user && !!campanaNombre.trim() && !!fecIniCam && !!fecFinCam;
  }, [user, campanaNombre, fecIniCam, fecFinCam]);

  const construirFilasDesdeDetalleOto = async (limit) => {
    // Importante: pedir 100k filas en una sola llamada suele gatillar 504 (API Gateway/Lambda timeout).
    // Traemos por páginas y acumulamos solo las filas elegibles.
    const pageSize = 1000;
    const maxPages = 500; // tope defensivo

    let page = 1;
    let elegibles = [];

    while (page <= maxPages) {
      const result = await getDetalleOto(user, { page, page_size: pageSize });
      const data = Array.isArray(result?.data) ? result.data : [];

      if (data.length === 0) break;

      for (const r of data) {
        const ef = String(r?.['ESTADO FILTRO FINAL'] ?? '').trim().toUpperCase();
        const resp = String(r?.['RESPONSABLE MGC'] ?? '').trim();
        if (ef === 'OK' && resp && resp.toUpperCase() !== 'NO APLICA') {
          elegibles.push(r);
          if (Number.isFinite(limit) && elegibles.length >= limit) break;
        }
      }

      if (Number.isFinite(limit) && elegibles.length >= limit) break;
      if (data.length < pageSize) break; // última página
      page += 1;
    }

    const base = Number.isFinite(limit) ? elegibles.slice(0, limit) : elegibles;
    return base.map((r) => ({
      rut: String(r?.RUT ?? r?.['RUT'] ?? '').trim(),
      cliente: r?.CLIENTE ?? '',
      ejecutivo: r?.EJECUTIVO ?? '',
      equipo: r?.EQUIPO ?? '',
      responsable: r?.['RESPONSABLE MGC'] ?? '',
      nombre_cam: campanaNombre.trim(),
      origen: r?.['TAMAÑO FINAL'] ?? '',
      oferta: null, // pendiente
      fec_ini_cam: fecIniCam,
      fec_fin_cam: fecFinCam,
      estado: null,
      penetracion: r?.['CATEGORÍA PENETRACIÓN'] ?? null,
    }));
  };

  const cargar = async (mode) => {
    if (!canSubmit) {
      window.alert('Completa nombre de campaña y fechas.');
      return;
    }
    try {
      setLoading(true);
      setLastResult(null);
      const isTest = mode === 'test';
      const filas = await construirFilasDesdeDetalleOto(isTest ? 5 : null);
      if (filas.length === 0) {
        window.alert('No hay filas elegibles (ESTADO FILTRO FINAL = OK) para cargar.');
        return;
      }
      const payload = {
        mode: isTest ? 'test' : 'full',
        nombre_cam: campanaNombre.trim(),
        fec_ini_cam: fecIniCam,
        fec_fin_cam: fecFinCam,
        rows: filas,
      };
      const resp = await cargarCampaniaDirecto(user, payload);
      setLastResult(resp);
      window.alert('Carga enviada. Revisa el resultado en la pantalla de Campañas.');
    } catch (e) {
      console.error(e);
      window.alert(e?.message || 'No se pudo ejecutar la carga.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex items-baseline gap-4 mb-6 flex-wrap">
        <h1 className="text-2xl font-bold text-gray-800">Carga Campaña</h1>
        <p className="text-gray-600">Inserción directa a `gestion_cartera`</p>
        <Link to="/panel-marketing/seguimiento-ejecutivo" className="btn-primary text-sm py-2 px-3" style={{ textDecoration: 'none' }}>
          Ver seguimiento ejecutivo
        </Link>
      </div>

      <div className="seccion-datos">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nombre campaña</label>
            <input
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              value={campanaNombre}
              onChange={(e) => setCampanaNombre(e.target.value)}
              placeholder="Ej. Reactivar Dic. 24"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha inicio</label>
            <input
              type="date"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              value={fecIniCam}
              onChange={(e) => setFecIniCam(e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Fecha fin</label>
            <input
              type="date"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              value={fecFinCam}
              onChange={(e) => setFecFinCam(e.target.value)}
            />
          </div>
        </div>

        <div className="mt-4 text-sm text-gray-600">
          Usuario: <span className="font-medium text-gray-800">{user?.nombre || '—'}</span>
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          <button type="button" className="btn-primary" disabled={!canSubmit || loading} onClick={() => cargar('test')}>
            {loading ? 'Cargando…' : 'Cargar prueba (5)'}
          </button>
          <button type="button" className="btn-primary" disabled={!canSubmit || loading} onClick={() => cargar('full')}>
            {loading ? 'Cargando…' : 'Cargar normal (mes)'}
          </button>
        </div>

        {lastResult && (
          <pre className="mt-4 text-xs bg-gray-50 border border-gray-200 rounded p-3 overflow-auto">
            {JSON.stringify(lastResult, null, 2)}
          </pre>
        )}

        <p className="mt-3 text-xs text-gray-500 max-w-3xl">
          Esta pantalla es el punto de entrada del flujo. En el siguiente paso conectamos el endpoint
          `/marketing/carga-campania` (Lambda `bi-mgc-carga-campania-directo`) para insertar automáticamente los RUT
          que pasan los filtros de Detalle OTO.
        </p>
      </div>
    </div>
  );
};

export default CargaCampana;

